import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { PlusCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { DropdownList } from '@praetorian-chariot/ui';
import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@/components/Tooltip';
import type { DropdownOption } from '@/types/dropdown.types';

export interface GraphExplorerDropdownProps {
  options: DropdownOption[];
  selected?: DropdownOption | DropdownOption[];
  onSelect?: (option: DropdownOption) => void;
  onApply?: (selected: DropdownOption[]) => void;
  onClear?: () => void;
  className?: string;
  label: string;
  active?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  border?: boolean;
  listClassName?: string;
  title?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  multiSelect?: boolean;
  virtualized?: boolean;
  isPraetorianUser?: boolean;
  isLoadingOptions?: boolean;
  onDropdownStateChange?: (isOpen: boolean, id: string) => void;
  id?: string;
  anchorRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * GraphExplorer-specific dropdown with improved positioning and sizing
 * - Width: 480px (optimized for AS Name content)
 * - Max Height: 630px (50% increase for better viewing of option lists)
 * - Fixed positioning: anchors to the ClusterWeightControls panel for consistent placement
 * - Appears in the same location regardless of which filter is clicked
 */
export const GraphExplorerDropdown: React.FC<GraphExplorerDropdownProps> = ({
  options,
  selected,
  onSelect,
  onApply,
  onClear,
  className,
  label,
  active = false,
  size = 'xs',
  border = true,
  listClassName,
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  multiSelect = false,
  virtualized = false,
  isPraetorianUser,
  isLoadingOptions = false,
  onDropdownStateChange,
  id,
  anchorRef,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [tempSelected, setTempSelected] =
    React.useState<DropdownOption[]>(getTempSelected);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  // Track button position for dropdown placement
  const [buttonPosition, setButtonPosition] = useState<
    | {
        bottom: number;
        left: number;
        width: number;
      }
    | undefined
  >();

  React.useEffect(() => {
    if (isOpen) {
      setTempSelected(getTempSelected());
    }
    if (onDropdownStateChange && id) {
      onDropdownStateChange(isOpen, id);
    }
  }, [isOpen, onDropdownStateChange, id]);

  // Update button position when dropdown opens or window resizes
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setButtonPosition({
          bottom: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, false);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, false);
      };
    } else {
      setButtonPosition(undefined);
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setTempSelected(
          multiSelect && Array.isArray(selected)
            ? selected
            : selected
              ? [selected as DropdownOption]
              : []
        );
      }
    };

    if (isOpen) {
      // Use bubble phase (false) instead of capture phase (true) to avoid interfering with navigation
      document.addEventListener('mousedown', handleClickOutside, false);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, [isOpen, multiSelect, selected]);

  React.useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isTextTruncated =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(isTextTruncated);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [selected]);

  function getTempSelected() {
    return multiSelect && Array.isArray(selected)
      ? selected
      : selected
        ? [selected as DropdownOption]
        : [];
  }

  const handleSelect = (option: DropdownOption) => {
    if (!multiSelect) {
      onSelect?.(option);
      setIsOpen(false);
      return;
    }
  };

  const handleToggleCheckbox = (option: DropdownOption) => {
    if (multiSelect) {
      setTempSelected(prev => {
        if (option.isGroup && option.items) {
          const groupSelected = option.items.every(item =>
            prev.some(s => s.id === item.id)
          );

          if (groupSelected) {
            return prev.filter(
              item => !option.items?.some(groupItem => groupItem.id === item.id)
            );
          } else {
            return [
              ...prev.filter(
                item =>
                  !option.items?.some(groupItem => groupItem.id === item.id)
              ),
              ...option.items,
            ];
          }
        }

        const isSelected = prev.some(item => item.id === option.id);
        if (isSelected) {
          return prev.filter(item => item.id !== option.id);
        } else {
          return [...prev, option];
        }
      });
    }
  };

  const handleApply = () => {
    if (multiSelect && onApply) {
      const filteredSelection = tempSelected.filter(item => !item.isGroup);
      onApply(filteredSelection);
    }
    setIsOpen(false);
  };

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTempSelected([]);
    onClear?.();
  };

  const sizeClasses = {
    xs: 'py-1 px-2 text-xs',
    sm: 'py-1.5 px-3 text-sm',
    md: 'py-2 px-4 text-sm',
    lg: 'py-2.5 px-5 text-base',
    xl: 'py-3 px-6 text-base',
  };

  const renderSelectedContent = () => {
    if (!active) return null;

    const getDisplayText = () => {
      if (!multiSelect || !Array.isArray(selected)) {
        return (selected as DropdownOption).label || '';
      }

      const completeGroups = options.filter(
        option =>
          option.isGroup &&
          option.items?.every(item => selected.some(s => s.id === item.id))
      );

      const individualItems = selected.filter(
        item =>
          !item.isGroup &&
          !completeGroups.some(group =>
            group.items?.some(groupItem => groupItem.id === item.id)
          )
      );

      const labels: string[] = [
        ...completeGroups.map(group => group.label),
        ...individualItems.map(item => item.label),
      ];

      return labels.join(', ');
    };

    const selectedText = getDisplayText();

    return (
      <>
        <span className="mx-2 text-white">|</span>
        {isTruncated ? (
          <Tooltip
            title={
              <div className="w-full min-w-[200px]">
                {multiSelect && Array.isArray(selected) ? (
                  <ul className="space-y-1">
                    {selected
                      .filter(item => !item.isGroup)
                      .map((option, index) => (
                        <li key={option.id ?? index} className="text-sm">
                          {option.label}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <span className="text-sm">{selectedText}</span>
                )}
              </div>
            }
            placement="bottom"
          >
            <span ref={textRef} className="inline-block max-w-[200px] truncate">
              {selectedText}
            </span>
          </Tooltip>
        ) : (
          <span ref={textRef} className="inline-block max-w-[200px] truncate">
            {selectedText}
          </span>
        )}
        <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-white" />
      </>
    );
  };

  const processedOptions = React.useMemo(() => {
    return options.map(option => {
      if (option.isGroup && option.items) {
        const hasSelectedItems = option.items.some(item =>
          tempSelected.some(s => s.id === item.id)
        );

        return {
          ...option,
          checked: hasSelectedItems,
          items: option.items.map(item => ({
            ...item,
            checked: tempSelected.some(s => s.id === item.id),
          })),
        };
      }

      return {
        ...option,
        checked: tempSelected.some(s => s.id === option.id),
      };
    });
  }, [options, tempSelected]);

  // GraphExplorer-specific dimensions
  const DROPDOWN_WIDTH = 900; // Increased from 400px for AS Name content
  const DROPDOWN_HEIGHT = 950; // 50% increase from 420px (420 * 1.5 = 630)
  const DROPDOWN_GAP = 4; // Reduced gap for closer positioning

  const dropdownStyle = React.useMemo(() => {
    if (!isOpen) {
      return undefined;
    }

    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 0;

    // If we have an anchor ref, position relative to it (fixed position)
    if (anchorRef?.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();

      // Position dropdown immediately to the right of the panel (submenu style)
      let dropdownLeft = anchorRect.right - 160; // Shifted 200px left to overlap/align better

      // Find the search bar element to align with it
      const searchBar = anchorRef.current.querySelector(
        '[class*="SearchBar"]'
      ) as HTMLElement;
      let dropdownTop = anchorRect.top + 60; // Default fallback

      if (searchBar) {
        const searchBarRect = searchBar.getBoundingClientRect();
        // Align dropdown top with search bar top
        dropdownTop = searchBarRect.top;
      }

      // Check if dropdown would overflow screen right
      if (dropdownLeft + DROPDOWN_WIDTH > screenWidth - 16) {
        // Position to the left of panel instead
        dropdownLeft = anchorRect.left - DROPDOWN_WIDTH - 8;
      }

      // Ensure not off-screen left
      dropdownLeft = Math.max(16, dropdownLeft);

      // Check vertical overflow
      if (dropdownTop + DROPDOWN_HEIGHT > screenHeight - 16) {
        dropdownTop = Math.max(16, screenHeight - DROPDOWN_HEIGHT - 16);
      }

      return {
        top: `${dropdownTop}px`,
        left: `${dropdownLeft}px`,
        width: `${DROPDOWN_WIDTH}px`,
        pointerEvents: 'auto' as const,
      };
    }

    // Fallback: position relative to button if no anchor
    if (!buttonPosition) {
      return undefined;
    }

    const {
      bottom: buttonViewportBottom,
      left: buttonViewportLeft,
      width: buttonWidth,
    } = buttonPosition;

    let dropdownTop = buttonViewportBottom + DROPDOWN_GAP;
    let calculatedDropdownLeft = buttonViewportLeft;

    if (buttonViewportLeft + DROPDOWN_WIDTH > screenWidth - 16) {
      calculatedDropdownLeft =
        buttonViewportLeft + buttonWidth - DROPDOWN_WIDTH;
      if (calculatedDropdownLeft < 16) {
        calculatedDropdownLeft = Math.min(
          screenWidth - DROPDOWN_WIDTH - 16,
          Math.max(16, calculatedDropdownLeft)
        );
      }
    }

    calculatedDropdownLeft = Math.max(16, calculatedDropdownLeft);

    if (dropdownTop + DROPDOWN_HEIGHT > screenHeight - 16) {
      dropdownTop = Math.max(
        16,
        buttonPosition.bottom -
          buttonPosition.width -
          DROPDOWN_HEIGHT -
          DROPDOWN_GAP
      );
    }

    return {
      top: `${dropdownTop}px`,
      left: `${calculatedDropdownLeft}px`,
      width: `${DROPDOWN_WIDTH}px`,
      pointerEvents: 'auto' as const,
    };
  }, [
    isOpen,
    buttonPosition,
    anchorRef,
    DROPDOWN_WIDTH,
    DROPDOWN_HEIGHT,
    DROPDOWN_GAP,
  ]);

  return (
    <>
      <div className={twMerge('relative', className)}>
        <button
          ref={buttonRef}
          onClick={e => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          data-testid="graph-explorer-dropdown"
          className={twMerge(
            'flex max-w-full min-w-0 items-center rounded-full border border-dashed font-bold transition-colors duration-150',
            sizeClasses[size],
            active
              ? 'border-chariot-brand-primary bg-chariot-brand-primary text-white'
              : 'border-chariot-stroke text-chariot-text-secondary',
            className
          )}
        >
          {active ? (
            <XCircleIcon
              className="mr-2 h-5 w-5 shrink-0 text-white"
              onClick={handleClearClick}
            />
          ) : (
            <PlusCircleIcon className="text-chariot-text-secondary mr-2 h-5 w-5 shrink-0" />
          )}
          <span className="truncate">{label}</span>
          {renderSelectedContent()}
        </button>
      </div>

      {isOpen && dropdownStyle && (
        <div
          ref={dropdownRef}
          className="fixed top-0 left-0 z-20 h-0 w-full overflow-visible"
          style={{ pointerEvents: 'none' }}
        >
          <div
            className="fixed inset-0 z-10"
            style={{ pointerEvents: 'auto' }}
            onClick={() => {
              setIsOpen(false);
              setTempSelected(
                multiSelect && Array.isArray(selected)
                  ? selected
                  : selected
                    ? [selected as DropdownOption]
                    : []
              );
            }}
          />
          <div className="absolute z-30" style={dropdownStyle}>
            <DropdownList
              options={processedOptions}
              onSelect={handleSelect}
              onToggleCheckbox={handleToggleCheckbox}
              className={twMerge(
                'bg-chariot-background-primary border-chariot-brand-primary mt-2 rounded border shadow-lg',
                listClassName
              )}
              style={{ maxHeight: '630px' }}
              border={border}
              title={title}
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              searchPlaceholder={searchPlaceholder}
              multiSelect={multiSelect}
              onApply={handleApply}
              virtualized={virtualized}
              isPraetorianUser={isPraetorianUser}
              isLoadingOptions={isLoadingOptions}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default GraphExplorerDropdown;
